'use client';
import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from '@icons';
import './_dropdown.scss';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';

export default function Dropdown({
  id,
  label,
  longestOptionLabel = '',
  children,
  className = '',
  multiselect = true,
  ...props
}) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Polyfill for CSS Anchor Positioning (for Safari/Firefox)
  useEffect(() => {
    const isFullAnchorSupported =
      typeof CSS !== 'undefined' &&
      CSS.supports('anchor-name: --test') &&
      (CSS.supports('position-anchor: --test') || CSS.supports('position-area: top'));

    if (isFullAnchorSupported) return;

    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) return;

    function updatePosition() {
      if (menu.matches(':popover-open')) {
        const rect = button.getBoundingClientRect();
        menu.style.top = `${rect.bottom}px`;
        menu.style.left = `${rect.left}px`;
        menu.style.minWidth = `${rect.width}px`;
        menu.style.right = 'auto';
      }
    }

    const handleToggle = (e) => {
      if (e.newState === 'open') {
        updatePosition();
      }
    };

    menu.addEventListener('toggle', handleToggle);
    window.addEventListener('scroll', updatePosition, { capture: true, passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });

    return () => {
      menu.removeEventListener('toggle', handleToggle);
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  return (
    <div className={`Dropdown ${className}`.trim()} {...props}>
      {longestOptionLabel && (
        <span className="Dropdown__ghost-text">
          {multiselect && <span className="Dropdown__ghost-checkbox-space"></span>}
          <HtmlToJsx html={longestOptionLabel} />
          <span className="Dropdown__ghost-icon-space"></span>
        </span>
      )}
      <button
        ref={buttonRef}
        className="Dropdown__select-button"
        popoverTarget={`dropdown-${id}`}
        style={{ anchorName: `--dropdown-${id}` }}
      >
        <span className="Dropdown__select-title">
          <HtmlToJsx html={label} />
        </span>
        <span className="Dropdown__select-icon">
          <ChevronDown className="Dropdown__icon-closed" />
          <ChevronUp className="Dropdown__icon-open" />
        </span>
      </button>

      <div
        ref={menuRef}
        id={`dropdown-${id}`}
        className="Dropdown__select-menu"
        popover="auto"
        style={{ positionAnchor: `--dropdown-${id}` }}
        onClick={!multiselect ? () => menuRef.current?.hidePopover() : undefined}
      >
        {children}
      </div>
    </div>
  );
}
